import { gql, useLazyQuery } from "@apollo/client";
import { RadioGroup } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { Modal, SearchSelectInput } from "components";
import CustomPicker from "components/layouts/calendar-header/custom-picker";
import DayPicker from "components/layouts/calendar-header/day-picker";
import MonthPicker from "components/layouts/calendar-header/month-picker";
import QuarterPicker from "components/layouts/calendar-header/quarter-picker";
import WeekPicker from "components/layouts/calendar-header/week-picker";
import {
  ContractorPicker,
  ContractorUserPicker,
  DistrictPicker,
} from "containers";
import { useFormik } from "formik";
import _ from "lodash";
import { FC, useState } from "react";
import toast from "react-hot-toast";
import { classNames, useDownloadFile, wrapClick } from "utils";

const mailingLists = [
  {
    id: 1,
    title: "All Orders",
    description: "Last message sent 4 days ago",
    users: "2740 users",
  },
  {
    id: 2,
    title: "Pending Orders",
    description: "Last message sent an hour ago",
    users: "621 users",
  },
  {
    id: 3,
    title: "Assigned Orders",
    description: "Last message sent 2 weeks ago",
    users: "1200 users",
  },
  {
    id: 4,
    title: "In Progress Orders",
    description: "Last message sent 4 days ago",
    users: "2740 users",
  },
  {
    id: 5,
    title: "Resolved Orders",
    description: "Last message sent 4 days ago",
    users: "2740 users",
  },
  {
    id: 6,
    title: "Completed Orders",
    description: "Last message sent 4 days ago",
    users: "2740 users",
  },
];

const CREATE_EXPORT = gql`
  query GetExportFileUrl(
    $entityType: EntityType!
    $fields: [String]
    $filter: JSON
  ) {
    url: getExportFileUrl(
      entityType: $entityType
      fields: $fields
      filter: $filter
    )
  }
`;

const views = ["all-time", "day", "month", "custom"] as const;

interface ExportDataContainerProps {
  entityType: string;
  open: boolean;
  setOpen: (val: boolean) => void;
  filter: any;
}

const ExportDataContainer: FC<ExportDataContainerProps> = ({
  open,
  setOpen,
  filter,
  entityType,
}) => {
  const [selectedMailingLists, setSelectedMailingLists] = useState(
    mailingLists[0]
  );
  const [createDataExport, { loading }] = useLazyQuery(CREATE_EXPORT, {
    fetchPolicy: "no-cache",
  });

  const { downloadAction, downloadLoading } = useDownloadFile({
    onError: () => {
      toast(
        JSON.stringify({
          type: "error",
          title: "An error occurred downloading file",
        })
      );
    },
    getFileName: () => new Date().toISOString() + "_blocks.csv",
  });

  const exportForm = useFormik({
    initialValues: {
      type: "",
      periodType: "all-time",
      district: "",
      prepaidSyncStatus: "",
      category: "",
      priority: "",
      contractor: "",
      fromDate: "",
      toDate: "",
      dateField: "",
    },
    onSubmit: (values) => {
      createDataExport({
        variables: {
          entityType,
          filter: filter,
        },
        fetchPolicy: "no-cache",
      }).then(({ data }) => {
        console.log("datum", data);
        if (data.url) {
          downloadAction(`/public/${data.url}`);
          setOpen(false);
        } else {
          toast(
            JSON.stringify({ type: "error", title: "Could not export file" })
          );
        }
      });
    },
  });

  const clearForm = () => {
    exportForm.resetForm();
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={`Export ${_.startCase(entityType) || "Data"}`}
      description="Select fields to export"
      size={"5xl"}
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading || downloadLoading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(() => exportForm.handleSubmit())}
          >
            {loading
              ? "Exporting data..."
              : downloadLoading
              ? "Downlading file..."
              : "Export Data"}
          </button>
          <button
            type="button"
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(() => {
              // __addAllFields();
            })}
          >
            Select All
          </button>
          <button
            type="button"
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(() => {
              clearForm();
            })}
          >
            Clear All
          </button>
        </>
      )}
    >
      <div>
        <RadioGroup
          value={selectedMailingLists}
          onChange={setSelectedMailingLists}
        >
          <RadioGroup.Label className="text-base font-semibold leading-6 text-gray-900">
            Select an export type
          </RadioGroup.Label>

          <div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-3 sm:gap-4">
            {mailingLists.map((mailingList) => (
              <RadioGroup.Option
                key={mailingList.id}
                value={mailingList}
                className={({ active }) =>
                  classNames(
                    active
                      ? "border-primary-600 ring-2 ring-primary-600"
                      : "border-gray-300",
                    "relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none"
                  )
                }
              >
                {({ checked, active }) => (
                  <>
                    <span className="flex flex-1">
                      <span className="flex flex-col">
                        <RadioGroup.Label
                          as="span"
                          className="block text-sm font-medium text-gray-900"
                        >
                          {mailingList.title}
                        </RadioGroup.Label>
                        <RadioGroup.Description
                          as="span"
                          className="mt-1 flex items-center text-sm text-gray-500"
                        >
                          {mailingList.description}
                        </RadioGroup.Description>
                        {/* <RadioGroup.Description as="span" className="mt-6 text-sm font-medium text-gray-900">
                      {mailingList.users}
                    </RadioGroup.Description> */}
                      </span>
                    </span>
                    <CheckCircleIcon
                      className={classNames(
                        !checked ? "invisible" : "",
                        "h-5 w-5 text-primary-600"
                      )}
                      aria-hidden="true"
                    />
                    <span
                      className={classNames(
                        active ? "border" : "border-2",
                        checked ? "border-primary-600" : "border-transparent",
                        "pointer-events-none absolute -inset-px rounded-lg"
                      )}
                      aria-hidden="true"
                    />
                  </>
                )}
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>
      </div>

      <div className="border-b border-white/10 mt-8">
        <h2 className="text-base font-semibold leading-6 text-gray-900">
          Select filters to apply
        </h2>
        <p className="mt-1 text-sm leading-6 text-gray-600">
          Select filters to filter down the data exported
        </p>

        <div className="mt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
          <div className="sm:col-span-2">
            <DistrictPicker
              id="district"
              lockFirstItem={true}
              rawId={true}
              {...exportForm}
              position="top"
            />
          </div>
          <div className="sm:col-span-2">
            <ContractorPicker
              id="contractor"
              label={"Contractor"}
              placeholder="Select Contractor"
              filter={{ district: exportForm?.values?.district }}
              {...exportForm}
              position="top"
              rawId={true}
            />
          </div>
          {exportForm?.values?.contractor && (
            <div className="sm:col-span-2">
              <ContractorUserPicker
                id="contractorUser"
                label={"Contractor Agent"}
                placeholder="Select Agent"
                filter={{ contractor: exportForm?.values?.contractor }}
                {...exportForm}
                position="top"
                rawId={true}
              />
            </div>
          )}

          <div className="sm:col-span-2 sm:col-start-1">
            <SearchSelectInput
              id="category"
              options={[
                { label: { title: "Express" }, value: "Express" },
                { label: { title: "Premium" }, value: "Premium" },
                { label: { title: "Standard" }, value: "Standard" },
              ]}
              label={"Category"}
              placeholder="Select Category"
              {...exportForm}
              position="top"
            />
          </div>
          <div className="sm:col-span-2">
            <SearchSelectInput
              id="priority"
              options={[
                { label: { title: "Low" }, value: "Low" },
                { label: { title: "Medium" }, value: "Medium" },
                { label: { title: "High" }, value: "High" },
                { label: { title: "Critical" }, value: "Critical" },
              ]}
              label={"Priority"}
              placeholder="Select Priority"
              {...exportForm}
              position="top"
            />
          </div>
          <div className="sm:col-span-2">
            <SearchSelectInput
              id="prepaidSyncStatus"
              options={[
                { label: { title: "Pending" }, value: "Pending" },
                { label: { title: "Attempted" }, value: "Attempted" },
                {
                  label: { title: "Forward Synced" },
                  value: "ForwardSynced",
                },
                {
                  label: { title: "Reverse Synced" },
                  value: "ReverseSynced",
                },
                { label: { title: "Completed" }, value: "Completed" },
              ]}
              label={"Sync Status"}
              placeholder="Select Sync Status"
              {...exportForm}
              position="top"
            />
          </div>
          <div className="sm:col-span-2 sm:col-start-1">
            <SearchSelectInput
              id="periodType"
              options={views.map((view) => ({
                label: { title: _.upperFirst(view) },
                value: view,
              }))}
              label={"Period Type"}
              placeholder="Select Period Type"
              {...exportForm}
              position="top"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Period
            </label>
            <div className="mt-1">
              {exportForm?.values?.periodType === "day" && <DayPicker />}
              {exportForm?.values?.periodType === "week" && <WeekPicker />}
              {exportForm?.values?.periodType === "month" && <MonthPicker />}
              {exportForm?.values?.periodType === "quarter" && (
                <QuarterPicker />
              )}
              {exportForm?.values?.periodType === "custom" && <CustomPicker />}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ExportDataContainer;
